<template>
    <form :action="url" method="post" @submit.prevent="submit">
        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $t('messages.information') }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.name')" for="name" />
                        <FormInput
                            id="name"
                            v-model="form.name"
                            name="name"
                            type="text"
                            max="191"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.name" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.group')" for="group_id" />
                        <FormSelect
                            required
                            id="group_id"
                            v-model="form.group_id"
                            :options="options.groups"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.group_id" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-4">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.image')" for="image" />
                        <input
                            id="image"
                            @input="form.image = $event.target.files[0]"
                            name="image"
                            type="file"
                            max="191"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.image" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-12">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.description')" for="description" />
                        <FormTextarea
                            id="description"
                            v-model="form.description"
                            name="description"
                            type="text"
                            max="191"
                            class="mt-1 w-full"
                        />
                        <FormError :value="form.errors.description" class="mt-2" />
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $tc('messages.gamification_rewards', 2) }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.coins_amount')" for="coins_amount" />
                        <FormInput
                            id="coins_amount"
                            v-model.number="form.coins_amount"
                            name="coins_amount"
                            type="number"
                            step="1"
                            class="mt-1"
                        />
                        <FormError :value="form.errors.coins_amount" class="mt-2" />
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $t('messages.settings') }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-12">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.battle_reward_mode')" for="is_reward_evenly_split" />
                        <FormRadioGroupCards
                            name="is_reward_evenly_split"
                            v-model="form.is_reward_evenly_split"
                            :options="options.victoryRewardOptions"
                        />
                        <FormError :value="form.errors.is_reward_evenly_split" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.is_reward_shown')" for="is_reward_shown" />
                        <input
                            id="is_reward_shown"
                            type="checkbox"
                            v-model="form.is_reward_shown"
                            name="is_reward_shown"
                            class="focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                        />
                        <FormError :value="form.errors.is_reward_shown" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.max_actions_per_student')" for="max_actions_per_student" />
                        <FormInput
                            id="max_actions_per_student"
                            v-model.number="form.max_actions_per_student"
                            name="max_actions_per_student"
                            type="number"
                            min="0"
                            :placeholder="$t('messages.(optional)')"
                            class="mt-1"
                        />
                        <FormError :value="form.errors.max_actions_per_student" class="mt-2" />
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection>
            <template v-slot:title>
                {{ $t('messages.date') }}
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.started_at')" for="started_at" />
                        <FormInput
                            id="started_at"
                            v-model="form.started_at"
                            name="started_at"
                            type="datetime-local"
                            class="mt-1"
                        />
                        <FormError :value="form.errors.started_at" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.finished_at')" for="finished_at" />
                        <FormInput
                            id="finished_at"
                            v-model="form.finished_at"
                            name="finished_at"
                            type="datetime-local"
                            class="mt-1"
                        />
                        <FormError :value="form.errors.finished_at" class="mt-2" />
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <TitleFieldsFormSection>
            <template v-slot:title>
                <div>
                    {{ $t('messages.consequence') }}
                </div>
                <div class="mt-1 text-sm leading-5 text-gray-500">
                    {{ $t('messages.consequence_points_loss_explanation') }}
                </div>
            </template>
            <div class="md:grid md:grid-cols-12 md:gap-6">
                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.health_amount')" for="consequence_health_amount" />
                        <FormInput
                            id="consequence_health_amount"
                            v-model.number="form.consequence_health_amount"
                            min="-65000"
                            max="-1"
                            step="1"
                            :placeholder="$t('messages.(optional)')"
                            name="consequence_health_amount"
                            type="number"
                            class="mt-1"
                        />
                        <FormError :value="form.errors.consequence_health_amount" class="mt-2" />
                    </div>
                </div>

                <div class="sm:col-span-6">
                    <div class="mt-1">
                        <FormLabel :value="$t('messages.coins_amount')" for="coins_amount" />
                        <FormInput
                            id="consequence_coins_amount"
                            v-model.number="form.consequence_coins_amount"
                            min="-65000"
                            max="-1"
                            step="1"
                            :placeholder="$t('messages.(optional)')"
                            name="consequence_coins_amount"
                            type="number"
                            class="mt-1"
                        />
                        <FormError :value="form.errors.consequence_coins_amount" class="mt-2" />
                    </div>
                </div>
            </div>
        </TitleFieldsFormSection>

        <FetchQuestItemsTable
            :options="options"
            v-model="form.requestedItems"
            :errors="form.errors"
            @cost="updateCost"
        />

        <AlertMessage class="mt-5" type="info" v-if="cost !== null && form.coins_amount && form.group_id">
            <template v-slot:title>
                {{ $t('messages.information') }}
            </template>
            <template v-slot:body>
                <div v-if="form.is_reward_evenly_split">
                    <span>{{ $t('messages.each_student_receives_X_coins', { amount: form.coins_amount }) }}</span>
                </div>
                <div v-else>
                    <span>
                        {{ $t('messages.each_student_receives_X_coins_on_average', { amount: averageReward }) }}
                    </span>
                </div>
                <span>
                    {{ $t('messages.fetch_quest_cost_total_and_average', { total: cost, average: averageCost }) }}
                </span>
            </template>
        </AlertMessage>

        <div class="flex justify-end mt-5">
            <AppButton desired-action element="button" type="submit" :disabled="form.processing">
                {{ $t('messages.save') }}
                <span v-if="form.processing" class="ml-1 btn-spinner"></span>
            </AppButton>
        </div>
    </form>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import TitleFieldsFormSection from '@/components/TitleFieldsFormSection.vue';
import FormInput from '@/components/Forms/FormInput.vue';
import FormLabel from '@/components/Forms/FormLabel.vue';
import FormError from '@/components/Forms/FormError.vue';
import FormSelect from '@/components/Forms/FormSelect.vue';
import AppButton from '@/components/AppButton.vue';
import FormTextarea from '@/components/Forms/FormTextarea.vue';
import AlertMessage from '@/components/AlertMessage.vue';
import FormRadioGroupCards from '@/components/Forms/FormRadioGroupCards.vue';
import FetchQuestItemsTable from '@/Pages/Teacher/Gamification/FetchQuests/partials/FetchQuestItemsTable.vue';

export default {
    components: {
        FormRadioGroupCards,
        AlertMessage,
        TitleFieldsFormSection,
        FormInput,
        FormLabel,
        FormError,
        FormSelect,
        FormTextarea,
        AppButton,
        FetchQuestItemsTable,
    },
    layout: Layout,
    name: 'FetchQuestForm',
    props: {
        options: {
            required: true,
            type: Object,
        },
        fetchQuest: {
            required: true,
            type: Object,
        },
        isEdit: {
            required: false,
            default: false,
            type: Boolean,
        },
        url: {
            required: true,
            type: String,
        },
    },
    data() {
        return {
            form: this.$inertia.form({
                _method: this.isEdit ? 'patch' : 'post',
                name: this.fetchQuest.name,
                group_id: this.fetchQuest.group_id,
                description: this.fetchQuest.description,
                coins_amount: this.fetchQuest.coins_amount,
                is_reward_evenly_split: this.fetchQuest.is_reward_evenly_split,
                is_reward_shown: this.fetchQuest.is_reward_shown,
                max_actions_per_student: this.fetchQuest.max_actions_per_student,
                status: this.fetchQuest.status,
                started_at: this.fetchQuest.started_at,
                finished_at: this.fetchQuest.finished_at,
                requestedItems: this.fetchQuest.requestedItems,
                consequence_health_amount: this.fetchQuest.consequence_health_amount,
                consequence_coins_amount: this.fetchQuest.consequence_coins_amount,
                image: null,
            }),
            cost: null,
        };
    },
    methods: {
        submit() {
            this.form.post(this.url);
        },
        updateCost(cost) {
            this.cost = cost;
        },
    },
    computed: {
        averageCost: function () {
            if (!this.cost) {
                return null;
            }
            if (!this.selectedGroup) {
                return null;
            }

            return Math.round(this.cost / this.selectedGroup.students_count);
        },
        selectedGroup: function () {
            const group = this.options.groups.find((group) => group.value === this.form.group_id);
            if (!group) {
                return null;
            }
            return group;
        },
        averageReward: function () {
            if (!this.form.coins_amount) {
                return null;
            }
            if (!this.selectedGroup) {
                return null;
            }

            return Math.round(this.form.coins_amount / this.selectedGroup.students_count);
        },
        totalItems: function () {
            return this.form.items.reduce((total, item) => total + item.quantity, 0);
        },
    },
};
</script>
